import { Component, OnInit, OnDestroy, Input, inject } from '@angular/core'
import { NgStyle, AsyncPipe, NgClass } from '@angular/common';
import { merge, of } from 'rxjs'
import { isEqual } from 'lodash-es'

import { AppAvailability, ShapeType, StorageService } from '@nx-superprep/backend/firebase'
import { countTitle, log } from '@nx-superprep/utils'

import { ResponsiveComponent } from '../responsive.component'

import { TooltipModule } from 'ngx-bootstrap/tooltip'
import { ContentTile } from './content-tile'
import { TileElement } from './tile-element'

export const _defaultImageURL = '/assets/missing.svg'

export const elementFrom = (item: unknown) => { return { content: item } as unknown as ContentTile}

@Component({
  selector: 'nx-superprep-tile',
  standalone: true,
  templateUrl: './tile.component.html',
  styleUrls: ['./tile.component.scss'],
  imports: [
    NgStyle, NgClass, AsyncPipe,
    TooltipModule,
  ],
})
export class TileComponent extends ResponsiveComponent implements OnInit, OnDestroy {

  protected _tileClassName = ''
  @Input()
  get tileClassName() { return this._tileClassName }
  set tileClassName(value) { this._tileClassName = value }

  @Input() shape: ShapeType = 'round'
  @Input() resolution: string|undefined

  protected _tileType: 'rounded-small'|'rounded'|'badge'|'rectangle'|'default' = 'default';
  @Input()
  get tileType() { return this._tileType }
  set tileType(value) {
    const didChange = value !== this._tileType || this.shape !== (this._tileType === 'rectangle' ? 'rect' : 'round')
    this._tileType = value
    this.shape = this._tileType === 'rectangle' ? 'rect' : 'round'
    if (didChange) { this.assetize() }
  }

  @Input()
  get content() { return this.element.content }
  set content(value) {
    this.element = { content: value }
  }

  protected _element: TileElement = {}
  @Input()
  get element() { return this._element; }
  set element(value) {
    const didChange = !this._element || !isEqual(this._element.content, value?.content)
    this._element = value as TileElement
    if (value.content && 'appAvailability' in value.content) {
      this.isDevOnly = !(value.content.appAvailability as AppAvailability).LT_PROD_WEB
    }
    if (didChange) { this.assetize() }
  }

  isDevOnly = false
  protected _imageURL =_defaultImageURL

  storageService =  inject(StorageService)
  constructor() {
    super()
    super.rootClassName = 'tile-root-class-name'
  }

  override ngOnInit() {
    super.ngOnInit()
    this.isVisible = !!this.content && this.visible
  }

  assetize(name?: string, resolution = this.resolution, shape: ShapeType = this.shape) {
    name = this.image ?? _defaultImageURL
    this.storageService.getIconAsset(name, resolution, shape).then(a => this._imageURL = a.url)
  }

  onUnlock() {
    log.debug('unlock', this.element)
  }

  get isReducedWidth() { return !this.isWeb; }
  get isReducedHeight() { return !this.isWeb; }

  set isVisible(value: boolean) {
    if (value && !this.isVisible) { this.assetize(); }
    if (this.element?.visible) { this.element.visible = value; }
  }
  get visible() {
    return Boolean(this.element?.visible)
  }

  get isLocked() {
    return this.element?.locked
  }

  get isPlaceholder() {
    return !!this.content?.isPlaceholder
  }

  get image() {
    if (this.isPlaceholder) return '/assets/missing.svg';
    const _default = this.imageString ?? this.title
    return this.content
    ? ('image' in this.content)
      ? this.content?.image ?? _default
      : ('iconName' in this.content)
        ? this.content?.iconName ?? _default
        : ('imageString' in this.content)
          ? String(this.content?.imageString ?? this.title)
          : ('imageStringRect' in this.content)
            ? String(this.content?.imageStringRect ?? this.title)
            : _default
    : _default
  }

  get imageString() {
    return this.content ? ('imageString' in this.content) ? String(this.content?.imageString ?? this.title) : undefined : undefined
  }

  get title() {
    return this.content?.title
  }

  get tooltip() {
    return this.isWeb && (this.content?.title??'').length > 11 ? this.content?.title : undefined
  }

  get color() {
    return this.content?.color ?? 'black'
  }

  get backgroundColor() {
    return this.content?.isPlaceholder ? 'transparent' : this.content?.background ?? 'transparent';
  }

  get children() {
    return this.element?.children ?? []
  }

  get childName() {
    return this.element?.childName
  }

  get imageURL() {
    return merge(of(_defaultImageURL), of(this._imageURL))
  }

  get imageALT() {
    return this.title
  }

  get isFinishedForStudy() {
    return false
  }

  get countTitle() {
    return this.childName ? countTitle(this.children, this.childName, `${this.childName}s`) : this.children.length
  }

  get style() {
    return this.isPlaceholder
    ? { 'baclground-color': 'transparent', 'border': 'none' }
    : this.tileType === 'badge'
      ? { 'color': this.color, 'background-color': this.backgroundColor }
      : { 'background-color': this.backgroundColor }
  }
}
